import { ProFormColumnsType } from '@ant-design/pro-components';
import { CustomerDeclaration } from '@components/CustomerDeclaration/CustomerDeclaration';
import { Form } from 'antd';
import { BaseFormSchemaWithJsxMessagesProps } from '../types';

type SubmitDipFormSchemaProps = BaseFormSchemaWithJsxMessagesProps;

export const CUSTOMER_DECLARATION_ACCEPTED = 'customerDeclarationAccepted';

export const submitDipFormSchema = (props: SubmitDipFormSchemaProps): ProFormColumnsType[] => [
  {
    renderFormItem: () => (
      <h4 className={'mb-0'}>{props.formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.form.header' })}</h4>
    ),
  },
  {
    dataIndex: 'checkboxes',
    valueType: 'checkbox',
    className: 'mb-4',
    fieldProps: {
      className: 'flex flex-col gap-3 [&_.ant-checkbox-wrapper]:items-start [&_.ant-checkbox-wrapper]:ml-0!',
      options: [
        {
          label: props.formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.creditConsentAccepted.label' }),
          value: 'creditConsentAccepted',
        },
        {
          label: props.formatMessage(
            { id: 'btlApplicationForm.SUBMIT_DIP.privacyAccepted.label' },
            {
              privacyPolicyLink: (str: string) => (
                <a
                  target='_blank'
                  href='https://www.strideup.co/privacy-policy'
                  rel='noreferrer'
                  onClick={e => e.stopPropagation()}
                >
                  {str}
                </a>
              ),
            },
          ),
          value: 'privacyAccepted',
        },
      ],
    },
    formItemProps: {
      className: '[&_.ant-form-item-control-input]:overflow-visible [&_.ant-form-item-control-input]:mb-2',
      validateTrigger: 'onSubmit',
      rules: [
        {
          validator: (_, value) => {
            const allValues = ['creditConsentAccepted', 'privacyAccepted'];
            // The rejection message is deliberately empty -- rejecting still marks the field
            // invalid (so it's highlighted red), but leaves nothing for AntD/ProForm to render as
            // text here. The shouldUpdate item below is the one place the shared message renders,
            // so it can't show once per checkbox group.
            return allValues.every(v => value?.includes(v)) ? Promise.resolve() : Promise.reject(new Error(''));
          },
        },
      ],
    },
  },
  {
    renderFormItem: () => (
      // pointer-events-auto because a submitted case renders this form through makeFormReadonly,
      // which turns pointer events off -- without it the declaration can no longer be scrolled.
      <CustomerDeclaration
        className={
          'mb-4 max-h-96 overflow-y-auto rounded-lg border border-solid border-gray-200 p-4 pointer-events-auto'
        }
      />
    ),
  },
  {
    dataIndex: CUSTOMER_DECLARATION_ACCEPTED,
    valueType: 'checkbox',
    className: 'mb-4',
    fieldProps: {
      className: 'flex flex-col gap-3 [&_.ant-checkbox-wrapper]:items-start [&_.ant-checkbox-wrapper]:ml-0!',
      options: [
        {
          label: props.formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.customerDeclarationAccepted.label' }),
          value: CUSTOMER_DECLARATION_ACCEPTED,
        },
      ],
    },
    formItemProps: {
      // Tighter bottom margin than AntD's default, so the notes section below reads as the next
      // thing to do rather than as an afterthought.
      className: 'mb-2 [&_.ant-form-item-control-input]:overflow-visible [&_.ant-form-item-control-input]:mb-2',
      validateTrigger: 'onSubmit',
      rules: [
        {
          // Same reasoning as the checkboxes field above: empty rejection message, so this field's
          // own copy of the text never renders -- the shared message below is the only one that can.
          validator: (_, value) =>
            value?.includes(CUSTOMER_DECLARATION_ACCEPTED) ? Promise.resolve() : Promise.reject(new Error('')),
        },
      ],
    },
  },
  {
    // AntD reserves a control's worth of height and margin for every schema column, so without
    // this the empty message placeholder pushed the notes section a whole blank field below the
    // declaration -- far enough for brokers to miss the free text box.
    formItemProps: {
      className: 'mb-0 [&_.ant-form-item-control-input]:min-h-0',
    },
    renderFormItem: () => (
      // Both checkbox fields above validate independently (each is correctly highlighted red on
      // its own) but reject with an empty message, so this is the only place the shared text can
      // ever render -- it can't show twice no matter which/how many of the two groups are
      // incomplete. `shouldUpdate` only re-renders this on form changes; unlike `dependencies` on a
      // validated field, it never triggers validation itself, so it can't cause an error to appear
      // before Submit is pressed.
      <Form.Item noStyle shouldUpdate>
        {form => {
          const hasError =
            form.getFieldError('checkboxes').length > 0 || form.getFieldError(CUSTOMER_DECLARATION_ACCEPTED).length > 0;

          if (!hasError) {
            return null;
          }

          return (
            <div className={'ant-form-item-explain ant-form-item-explain-connected mb-4'}>
              <div className={'ant-form-item-explain-error'}>
                {props.formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.allTermsRequired.label' })}
              </div>
            </div>
          );
        }}
      </Form.Item>
    ),
  },
  // The notes section will only be displayed when the Broker is able to submit a DIP or the application
  // This is because we add this text to the Case Notes in the model and it's difficult to re-hydrate
  // a specific note to display here.
  {
    hideInForm: !props.isFormEditable,
    // Keeps the heading, the explanation and the text box reading as one block rather than three
    // items a form row apart.
    formItemProps: {
      className: 'mb-2',
    },
    renderFormItem: () => (
      <div>
        <h2 className={'mb-1'}>{props.formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.notes.header' })}</h2>
        <p>{props.formatMessage({ id: 'btlApplicationForm.SUBMIT_DIP.notes.message' })}</p>
      </div>
    ),
  },
  {
    hideInForm: !props.isFormEditable,
    dataIndex: 'notes',
    valueType: 'textarea',
    fieldProps: {
      autoSize: {
        minRows: 6,
        maxRows: 30,
      },
    },
  },
];
